import {
  httpGet,
  httpPost,
  httpPatch,
  httpPatchMerge,
  httpDelete,
} from './http'
import type { ISelectItem, TEntityId } from '~/types/models'
import type { IActivityEmail } from '~/types/email/models'
import { splitDelimitedString } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'
import { suppressedRecipientErrorFrom } from './caseReply'
import { pickedDocumentIds } from '~/cynqueModules/caseReplyAttachments'
import type { EmailReadStatusIds } from './selectLists/emailReadStatii'

const apiEntityPath = '/api/emails'

// export async function getEmails({ apiPath = '/api/calls' } = {}) {
//   const callingMn = getEmails.name
//   return httpGet<IActivityEmail[]>({ callingMn, apiPath })
// }

const RECIPIENT_LISTS = ['toAddresses', 'ccAddresses', 'bccAddresses'] as const

/**
 * The email dialog's values as the request carries them: each recipient list
 * the form saved as ONE comma-separated string goes as the list of addresses
 * IActivityEmail declares. Only the lists present are
 * touched -- on an edit, the ones that changed -- and an emptied list goes as
 * [], which clears it, rather than being left out.
 *
 * The form's `documents` picker goes as `documentIds`, the server's name, and
 * only when something is picked.
 */
export function activityEmailRequestBody<T extends Partial<IActivityEmail>>(
  values: T
): T {
  const body: Record<string, unknown> = { ...values }
  for (const name of RECIPIENT_LISTS) {
    if (name in values) {
      body[name] = splitDelimitedString(values[name])
    }
  }
  if ('documents' in body) {
    const documentIds = pickedDocumentIds(body.documents)
    delete body.documents
    if (documentIds.length) {
      body.documentIds = documentIds
    }
  }
  return body as T
}

/**
 * The lists as sent, written back onto the object the dialog sent -- IN PLACE,
 * as services/opportunityRegistration.ts does for its email: the save path
 * keeps that object as the card's row, and it would otherwise hold the form's
 * comma-separated strings where the overview holds lists.
 */
function writeBackRecipientLists(
  sent: Partial<IActivityEmail>,
  requestBody: Partial<IActivityEmail>
) {
  for (const name of RECIPIENT_LISTS) {
    if (name in requestBody) {
      sent[name] = requestBody[name]
    }
  }
}

/**
 * A save that SENDS (status Ready to Send) refuses a suppressed recipient with a
 * 409, as a case reply does. Typed as that, not as the version conflict every
 * other 409 here is -- whose message, "changed by someone else", would be wrong.
 */
async function rethrowingSuppression<T>(
  apiPath: string,
  request: () => Promise<T>
): Promise<T> {
  try {
    return await request()
  } catch (error) {
    throw suppressedRecipientErrorFrom(error, apiPath) ?? error
  }
}

export async function createEmail(entity: IActivityEmail) {
  const callingMn = createEmail.name
  const apiPath = apiEntityPath
  const requestBody = activityEmailRequestBody(entity)
  const created = await rethrowingSuppression(apiPath, () =>
    httpPost<IActivityEmail>({
      callingMn,
      apiPath,
      entity: requestBody,
    })
  )
  writeBackRecipientLists(entity, requestBody)
  return created
}

export async function updateEmail(
  entityData: Partial<IActivityEmail>,
  id: TEntityId
) {
  const callingMn = updateEmail.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIEmail = Partial<IActivityEmail>
  const requestBody = activityEmailRequestBody(entityData)
  const updated = await rethrowingSuppression(apiPath, () =>
    httpPatchMerge<TPartialIEmail>({
      callingMn,
      apiPath,
      entityData: requestBody,
    })
  )
  writeBackRecipientLists(entityData, requestBody)
  return updated
}

export async function deleteEmail(entityData: IActivityEmail) {
  const callingMn = deleteEmail.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}

/**
 * Mark an inbound email Read or Unread (cynque-api-server #1814) -- the only
 * changes a person makes; Replied is the server's, set by a reply.
 *
 * Its own sub-resource rather than the email PATCH above, and with no
 * `version`: two people opening one message at once must not 409, and the
 * change must not bump the email's version under someone editing it. The
 * server answers Unread -> Read, Read -> Unread and a repeat of the current
 * value; it refuses Replied, anything out of Replied, and any email that is not
 * inbound (canChangeEmailReadStatus is the client's half).
 *
 * Works on ANY email id -- a case's, an activity's -- since one table holds
 * them all.
 */
export async function setEmailReadStatus({
  emailId,
  readStatusId,
}: {
  emailId: TEntityId
  readStatusId: EmailReadStatusIds.UNREAD | EmailReadStatusIds.READ
}): Promise<{ readStatus: ISelectItem | null }> {
  const callingMn = setEmailReadStatus.name
  return (await httpPatch<{ id: number }>({
    callingMn,
    apiPath: `${apiEntityPath}/${emailId}/readStatus`,
    entityData: { id: readStatusId },
  })) as unknown as { readStatus: ISelectItem | null }
}
